/**
 * collectValidationMessages.js
 *
 * Converts raw structured validation errors (used for inline field highlighting)
 * into a flat { sectionName: string[] } hashmap of human-readable messages
 * for the notification modal.
 */

// Pluralize helper: pluralize(3, "employee is", "employees are") => "3 employees are"
const pluralize = (count, singular, plural) =>
  count === 1 ? `1 ${singular}` : `${count} ${plural}`;

// Count rows where a specific error field is truthy (non-empty string, non-null)
const countFieldErrors = (rows, fieldName) =>
  rows.filter((r) => r.errors?.[fieldName] && r.errors[fieldName] !== "")
    .length;

// Add section only if it has messages
const addSection = (messages, sectionName, msgs) => {
  const filtered = msgs.filter(Boolean);
  if (filtered.length > 0) {
    messages[sectionName] = filtered;
  }
};

// Deduplicate rows by a given ID field to work around the pre-existing
// duplicate-push bug in delEmployeeValidation.js (lines 44-46, 111-113)
const deduplicateById = (rows, idField) => {
  const seen = new Set();
  return rows.filter((row) => {
    const id = row[idField];
    if (id == null || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

/**
 * Main export: converts the raw errors object from validateDelSubmitted / validateDraftDel
 * into { sectionName: string[] } for the notification modal.
 *
 * @param {Object} errors - The raw errors object from delValidation.js
 * @returns {Object} A hashmap of { sectionName: string[] }
 */
export const collectValidationMessages = (errors) => {
  if (!errors) return {};

  const messages = {};

  collectProjectInfo(messages, errors.projectInfo);
  collectEmployees(messages, errors.employees);
  collectUnits(messages, errors.units);
  collectMiscUnits(messages, errors.miscUnits);
  collectRentalUnits(messages, errors.rentalUnits);
  collectProjectSpecificUnits(messages, errors.projectSpecificUnits);
  collectFieldNotes(messages, errors.fieldNotes);
  collectClientSignatures(messages, errors.clientSignatures);
  collectJsa(messages, errors.jsa);
  collectWasteDisposal(messages, errors.wasteDisposal);

  return messages;
};

// --- Section collectors ---

function collectProjectInfo(messages, projectInfo) {
  if (!projectInfo || typeof projectInfo !== "object") return;
  const vals = Object.values(projectInfo).filter(Boolean);
  addSection(messages, "Project Info", vals);
}

function collectEmployees(messages, employees) {
  if (!employees || !Array.isArray(employees) || employees.length === 0) return;

  // Check for the "general" case (no employees at all)
  const generalError = employees.find((e) => e.employeeRowId === null);
  if (generalError) {
    addSection(messages, "Employees", [generalError.errors?.general]);
    return;
  }

  // Deduplicate to handle the pre-existing duplicate push bug
  const deduplicated = deduplicateById(employees, "employeeRowId");

  const msgs = [];
  const idCount = countFieldErrors(deduplicated, "employeeId");
  if (idCount > 0) {
    msgs.push(
      `${pluralize(idCount, "employee is", "employees are")} missing an Employee selection`
    );
  }

  const posCount = countFieldErrors(deduplicated, "employeePosition");
  if (posCount > 0) {
    msgs.push(
      `${pluralize(posCount, "employee is", "employees are")} missing a Position`
    );
  }

  const shiftErrCount = countFieldErrors(deduplicated, "employeeShiftError");
  if (shiftErrCount > 0) {
    msgs.push(
      `${pluralize(shiftErrCount, "employee has", "employees have")} no shifts entered`
    );
  }

  const invalidShiftCount = deduplicated.filter(
    (r) => r.errors?.shifts && r.errors.shifts.length > 0
  ).length;
  if (invalidShiftCount > 0) {
    msgs.push(
      `${pluralize(invalidShiftCount, "employee has", "employees have")} invalid shift times`
    );
  }

  addSection(messages, "Employees", msgs);
}

function collectUnits(messages, units) {
  if (!units || !Array.isArray(units) || units.length === 0) return;

  // Check for the "general" case (no units at all)
  const generalError = units.find((u) => u.unitRowId === null);
  if (generalError) {
    addSection(messages, "Units", [generalError.errors?.general]);
    return;
  }

  const msgs = [];
  const idCount = countFieldErrors(units, "unitId");
  if (idCount > 0) {
    msgs.push(
      `${pluralize(idCount, "unit is", "units are")} missing a Unit selection`
    );
  }

  const billableCount = countFieldErrors(units, "billableUnits");
  if (billableCount > 0) {
    msgs.push(
      `${pluralize(billableCount, "unit has", "units have")} missing or invalid Quantity`
    );
  }

  addSection(messages, "Units", msgs);
}

function collectMiscUnits(messages, miscUnits) {
  if (!miscUnits || !Array.isArray(miscUnits) || miscUnits.length === 0) return;

  const msgs = [];
  const nameCount = countFieldErrors(miscUnits, "unitName");
  if (nameCount > 0) {
    msgs.push(
      `${pluralize(nameCount, "misc. equipment row is", "misc. equipment rows are")} missing a Name`
    );
  }

  const qtyCount = countFieldErrors(miscUnits, "quantity");
  if (qtyCount > 0) {
    msgs.push(
      `${pluralize(qtyCount, "misc. equipment row has", "misc. equipment rows have")} missing or invalid Quantity`
    );
  }

  const qtyTypeCount = countFieldErrors(miscUnits, "quantityType");
  if (qtyTypeCount > 0) {
    msgs.push(
      `${pluralize(qtyTypeCount, "misc. equipment row has", "misc. equipment rows have")} missing or invalid Quantity Type`
    );
  }

  addSection(messages, "Misc. Equipment", msgs);
}

function collectRentalUnits(messages, rentalUnits) {
  if (
    !rentalUnits ||
    !Array.isArray(rentalUnits) ||
    rentalUnits.length === 0
  )
    return;

  const msgs = [];
  const nameCount = countFieldErrors(rentalUnits, "unitName");
  if (nameCount > 0) {
    msgs.push(
      `${pluralize(nameCount, "rental equipment row is", "rental equipment rows are")} missing a Name`
    );
  }

  const qtyCount = countFieldErrors(rentalUnits, "quantity");
  if (qtyCount > 0) {
    msgs.push(
      `${pluralize(qtyCount, "rental equipment row has", "rental equipment rows have")} missing or invalid Quantity`
    );
  }

  const qtyTypeCount = countFieldErrors(rentalUnits, "quantityType");
  if (qtyTypeCount > 0) {
    msgs.push(
      `${pluralize(qtyTypeCount, "rental equipment row has", "rental equipment rows have")} missing or invalid Quantity Type`
    );
  }

  const vendorCount = countFieldErrors(rentalUnits, "vendor");
  if (vendorCount > 0) {
    msgs.push(
      `${pluralize(vendorCount, "rental equipment row is", "rental equipment rows are")} missing a Vendor`
    );
  }

  addSection(messages, "Rental Equipment", msgs);
}

function collectProjectSpecificUnits(messages, projectSpecificUnits) {
  if (
    !projectSpecificUnits ||
    !Array.isArray(projectSpecificUnits) ||
    projectSpecificUnits.length === 0
  )
    return;

  const msgs = [];
  const unitCount = countFieldErrors(projectSpecificUnits, "unitNumber");
  if (unitCount > 0) {
    msgs.push(
      `${pluralize(unitCount, "project unit row is", "project unit rows are")} missing a Unit selection`
    );
  }

  const qtyCount = countFieldErrors(projectSpecificUnits, "quantity");
  if (qtyCount > 0) {
    msgs.push(
      `${pluralize(qtyCount, "project unit row has", "project unit rows have")} missing or invalid Quantity`
    );
  }

  addSection(messages, "Project Specific Units", msgs);
}

function collectFieldNotes(messages, fieldNotes) {
  if (
    !fieldNotes ||
    !Array.isArray(fieldNotes) ||
    fieldNotes.length === 0
  )
    return;

  const msgs = [];
  const timeCount = countFieldErrors(fieldNotes, "time");
  if (timeCount > 0) {
    msgs.push(
      `${pluralize(timeCount, "field note activity has", "field note activities have")} an invalid Time format`
    );
  }

  addSection(messages, "Field Notes", msgs);
}

function collectClientSignatures(messages, clientSignatures) {
  if (
    !clientSignatures ||
    !Array.isArray(clientSignatures) ||
    clientSignatures.length === 0
  )
    return;

  const msgs = [];
  const nameCount = countFieldErrors(clientSignatures, "clientEmpName");
  if (nameCount > 0) {
    msgs.push(
      `${pluralize(nameCount, "client signature is", "client signatures are")} missing a Name`
    );
  }

  const companyCount = countFieldErrors(
    clientSignatures,
    "clientCompanyName"
  );
  if (companyCount > 0) {
    msgs.push(
      `${pluralize(companyCount, "client signature is", "client signatures are")} missing a Company Name`
    );
  }

  const sigCount = countFieldErrors(clientSignatures, "clientEmpSig");
  if (sigCount > 0) {
    msgs.push(
      `${pluralize(sigCount, "client signature is", "client signatures are")} missing a Signature`
    );
  }

  addSection(messages, "Client Signatures", msgs);
}

function collectJsa(messages, jsa) {
  if (!jsa || typeof jsa !== "object") return;

  collectJsaBasicInfo(messages, jsa.basicInfo);
  collectJsaRac(messages, jsa.rac);
  collectJsaTraining(messages, jsa.training);
  collectJsaEmployeeSignatures(messages, jsa.employeeSignatures);
  collectJsaNonEmployeeSignatures(messages, jsa.nonEmployeeSignatures);
  collectJsaConfinedSpace(messages, jsa.confinedSpace);
  collectJsaHazardsV2(messages, jsa.hazardsV2);
}

function collectJsaBasicInfo(messages, basicInfo) {
  if (!basicInfo || typeof basicInfo !== "object") return;
  const vals = Object.values(basicInfo).filter(Boolean);
  addSection(messages, "JSA - Basic Info", vals);
}

function collectJsaRac(messages, rac) {
  if (!rac || typeof rac !== "object") return;
  const invalidCount = Object.values(rac).filter(Boolean).length;
  if (invalidCount > 0) {
    addSection(messages, "JSA - RAC Codes", [
      `${pluralize(invalidCount, "RAC code has", "RAC codes have")} an invalid value`,
    ]);
  }
}

function collectJsaTraining(messages, training) {
  if (!training || typeof training !== "object") return;

  const msgs = [];

  if (training.ewiTrainerId) {
    msgs.push(training.ewiTrainerId);
  }

  if (training.jsaTrainingEmployeesInfo) {
    if (typeof training.jsaTrainingEmployeesInfo === "string") {
      msgs.push(training.jsaTrainingEmployeesInfo);
    } else if (Array.isArray(training.jsaTrainingEmployeesInfo)) {
      const count = training.jsaTrainingEmployeesInfo.filter(
        (r) => r.errors?.employeeId && r.errors.employeeId !== ""
      ).length;
      if (count > 0) {
        msgs.push(
          `${pluralize(count, "training employee is", "training employees are")} missing an Employee selection`
        );
      }
    }
  }

  addSection(messages, "JSA - Training", msgs);
}

function collectJsaEmployeeSignatures(messages, employeeSignatures) {
  if (
    !employeeSignatures ||
    !Array.isArray(employeeSignatures) ||
    employeeSignatures.length === 0
  )
    return;

  const msgs = [];
  const idCount = countFieldErrors(employeeSignatures, "employeeId");
  if (idCount > 0) {
    msgs.push(
      `${pluralize(idCount, "employee signature is", "employee signatures are")} missing an Employee`
    );
  }

  const sigCount = countFieldErrors(employeeSignatures, "employeeSig");
  if (sigCount > 0) {
    msgs.push(
      `${pluralize(sigCount, "employee signature is", "employee signatures are")} missing a Signature`
    );
  }

  addSection(messages, "JSA - Employee Signatures", msgs);
}

function collectJsaNonEmployeeSignatures(messages, nonEmployeeSignatures) {
  if (
    !nonEmployeeSignatures ||
    !Array.isArray(nonEmployeeSignatures) ||
    nonEmployeeSignatures.length === 0
  )
    return;

  const msgs = [];
  const nameCount = countFieldErrors(nonEmployeeSignatures, "nonEmployeeName");
  if (nameCount > 0) {
    msgs.push(
      `${pluralize(nameCount, "non-employee signature is", "non-employee signatures are")} missing a Name`
    );
  }

  const companyCount = countFieldErrors(
    nonEmployeeSignatures,
    "nonEmployeeCompany"
  );
  if (companyCount > 0) {
    msgs.push(
      `${pluralize(companyCount, "non-employee signature is", "non-employee signatures are")} missing a Company`
    );
  }

  const sigCount = countFieldErrors(nonEmployeeSignatures, "nonEmployeeSig");
  if (sigCount > 0) {
    msgs.push(
      `${pluralize(sigCount, "non-employee signature is", "non-employee signatures are")} missing a Signature`
    );
  }

  addSection(messages, "JSA - Non-Employee Signatures", msgs);
}

function collectJsaConfinedSpace(messages, confinedSpace) {
  if (!confinedSpace || !Array.isArray(confinedSpace) || confinedSpace.length === 0)
    return;
  // confinedSpace is a simple array of error strings
  addSection(messages, "JSA - Confined Space", confinedSpace.filter(Boolean));
}

function collectJsaHazardsV2(messages, hazardsV2) {
  if (!hazardsV2 || typeof hazardsV2 !== "object") return;
  const vals = Object.values(hazardsV2).filter(Boolean);
  addSection(messages, "JSA - Hazards", vals);
}

function collectWasteDisposal(messages, wasteDisposal) {
  if (!wasteDisposal || typeof wasteDisposal !== "object") return;
  const vals = Object.values(wasteDisposal).filter(Boolean);
  addSection(messages, "Waste Disposal", vals);
}
